// Images to PDF (FR-16): page sizes, margins and where each image goes. Pure:
// no pdf-lib, so the page can show its preview without the PDF library.

export type PageSize = "a4" | "letter" | "fit";
export type Orientation = "portrait" | "landscape";
export type Margin = "none" | "small" | "large";

/** In points (1/72 inch), portrait. */
export const PAGE_SIZES = {
  a4: { width: 595.28, height: 841.89 },
  letter: { width: 612, height: 792 },
} as const;

export const MARGINS: Record<Margin, number> = {
  none: 0,
  small: 18,
  large: 36,
};

/** Screen pixels are 96 per inch; PDF points are 72 per inch. */
export const POINTS_PER_PIXEL = 72 / 96;

export type Box = { x: number; y: number; width: number; height: number };
type Size = { width: number; height: number };

export type ImagesToPdfOptions = {
  pageSize: PageSize;
  orientation: Orientation;
  margin: Margin;
};

export function pageFor(image: Size, o: ImagesToPdfOptions): Size {
  const m = MARGINS[o.margin] * 2;
  if (o.pageSize === "fit") {
    return {
      width: image.width * POINTS_PER_PIXEL + m,
      height: image.height * POINTS_PER_PIXEL + m,
    };
  }
  const { width, height } = PAGE_SIZES[o.pageSize];
  return o.orientation === "portrait"
    ? { width, height }
    : { width: height, height: width };
}

/** The image as large as fits inside the margins, centred (PDF y is from the bottom). */
export function placeImage(page: Size, image: Size, margin: Margin): Box {
  const m = MARGINS[margin];
  const room = { width: page.width - 2 * m, height: page.height - 2 * m };
  const scale = Math.min(room.width / image.width, room.height / image.height);
  const width = image.width * scale;
  const height = image.height * scale;
  return {
    x: (page.width - width) / 2,
    y: (page.height - height) / 2,
    width,
    height,
  };
}

export type PdfImage = { bytes: Uint8Array; type: "jpg" | "png" };
